import { Heart, MapPin, Sparkles, ChevronDown, Moon, Sun, Quote, Mail, Phone, ArrowRight, FileDown, ExternalLink, Send, Gift, BookOpen, Facebook } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { IMAGES, TAGLINE, EXPERIENCE_CARDS, WORKSHOP_BOXES, EBOOK_URL, COROMIND_URL, VERNA_SITE } from "./data";
import { BLOG_POSTS } from "./blog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Calendar } from "../ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { OrganicWave, Flourish, GoldHeart } from "./Decor";
import WorkshopModal from "./WorkshopModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Bullet = ({ children }) => (
  <li className="flex gap-3 items-start text-[0.95rem] leading-relaxed text-wine">
    <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[var(--lh-gold)] flex-shrink-0" />
    <span>{children}</span>
  </li>
);

/* ============= HERO ============= */
export const Hero = () => (
  <section id="welcome" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
    <div className="absolute inset-0 hero-overlay" />

    <div className="relative z-10 container-lh pt-28 pb-44 sm:pt-32 sm:pb-56 text-white text-center">
      <p className="eyebrow fade-up" style={{ color: "#284f3f", textShadow: "0 2px 12px rgba(255,255,255,0.4)" }}>
        A sacred journey on the Coromandel Peninsula, New Zealand
      </p>
      <h1
        className="text-2xl sm:text-4xl lg:text-5xl font-bold leading-[1.15] mt-6 fade-up uppercase mx-auto"
        style={{ fontFamily: '"Manrope", sans-serif', letterSpacing: "0.04em", color: "var(--lh-butter)", textShadow: "0 4px 30px rgba(0,0,0,0.45)" }}
      >
        <span className="block">Welcome to the</span>
        <span className="block">Light-Hearted Healing Centre</span>
        <span className="block">&amp; Expansion School</span>
      </h1>
      <p className="mt-6 text-lg sm:text-xl max-w-2xl mx-auto fade-up" style={{ color: "var(--lh-butter)" }}>
        7 days of nourishing your mind, body and soul on New Zealand's breathtaking Coromandel.
      </p>
      <p className="mt-3 text-base sm:text-lg max-w-2xl mx-auto italic fade-up" style={{ color: "var(--lh-butter)", opacity: 0.92 }}>
        Come for your well-being, awaken your spirit, and have a wonderful time doing it.
      </p>
      <div className="mt-9 flex flex-col sm:flex-row gap-4 fade-up justify-center">
        <button data-testid="hero-cta-explore" onClick={() => scrollTo("retreats")} className="btn-primary">
          Explore the Retreat <ArrowRight className="w-4 h-4" />
        </button>
        <button data-testid="hero-cta-dates" onClick={() => scrollTo("dates")} className="btn-outline-light">
          View Dates & Pricing
        </button>
      </div>

      <div className="mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-2 border-t border-white/30 pt-6 text-[0.78rem] sm:text-sm uppercase tracking-[0.32em] font-light"
        style={{ color: "var(--lh-butter)" }}>
        <span>Love Yourself</span><span className="text-[var(--lh-gold)]">✦</span>
        <span>Love Your Life</span><span className="text-[var(--lh-gold)]">✦</span>
        <span>Love Your Spirit</span>
      </div>
    </div>

    <button onClick={() => scrollTo("about")} className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:block" aria-label="Scroll down" style={{ color: "var(--lh-butter)" }}>
      <ChevronDown className="w-6 h-6 animate-bounce" />
    </button>
  </section>
);

/* ============= ABOUT ============= */
export const About = () => (
  <>
    <section id="about" data-testid="about-section" className="py-24 sm:py-32" style={{ background: "#f1e3e0" }}>
      <div className="container-lh max-w-4xl text-center">
        {/* 1. Welcome to Coromandel eyebrow — at the top */}
        <p className="eyebrow" style={{ color: "#284f3f" }}>Welcome to the Coromandel Peninsula, New Zealand</p>

        {/* 2. Heart logo */}
        <div className="flex justify-center my-8">
          <div className="relative">
            <div className="absolute inset-0 rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, var(--lh-gold) 0%, transparent 65%)" }} />
            <img
              src={IMAGES.heartLogo}
              alt="Light-Hearted Healing Centre — Hands holding a gold heart with pink and crimson energy"
              className="relative w-64 sm:w-80 h-auto object-contain mx-auto logo-clean"
              data-testid="about-logo"
            />
          </div>
        </div>

        {/* 3. Gold pill */}
        <div className="mb-7">
          <span className="pill-gold inline-block" style={{ whiteSpace: "normal", lineHeight: 1.3, padding: "0.75rem 1.8rem", fontSize: "0.78rem" }}>
            Light-Hearted Healing Centre and Expansion School
          </span>
        </div>

        {/* 4. Heading */}
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.1]" style={{ color: "var(--lh-coral)" }}>
          <span className="block">Light-Hearted Healing Centre</span>
          <span className="block"><span className="italic">&amp;</span> Expansion School</span>
        </h2>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto text-wine">
          {[
            "7 Days of Bliss",
            "7 Days of Holiday",
            "7 Days of Spiritual Awakening",
            "6 Nights at Flaxmill Bay",
            "18 Catered Meals",
            "Limited to 12 People",
          ].map((t) => (
            <div key={t} className="bg-white/70 border border-[var(--lh-gold)]/30 rounded-2xl px-4 py-4 text-sm sm:text-base font-medium">
              <Sparkles className="w-4 h-4 text-gold inline-block mr-2" />
              {t}
            </div>
          ))}
        </div>

        <p className="mt-10 font-display italic text-2xl sm:text-3xl text-plum font-bold">
          Coromandel road trip? Yes please — this is just what I need right now.
        </p>

        <p className="mt-7 font-script text-3xl text-coral">{TAGLINE}</p>
      </div>
    </section>
    <OrganicWave color="#ffffff" />
  </>
);

/* ============= WELCOME LONG-FORM ============= */
export const WelcomeLongForm = () => (
  <>
  <section id="welcome-long" data-testid="welcome-longform" className="bg-white">
    <div className="h-72 sm:h-96 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.welcomeStretch})` }} />
    <div className="container-lh py-24 sm:py-28 max-w-4xl">
      <Flourish color="#cca300" />
      <h2 className="font-display text-4xl sm:text-5xl text-center leading-tight mt-6" style={{ color: "var(--lh-coral)" }}>
        Welcome to the Light-Hearted Healing Centre & Expansion School
      </h2>
      <p className="mt-6 font-display italic text-2xl text-center font-bold" style={{ color: "var(--lh-coral)" }}>
        <strong className="not-italic font-bold" style={{ color: "var(--lh-coral)" }}>Light-Hearted</strong> — because that's who we are: fun, playful, with hearts full of light.
      </p>

      <div className="mt-12 space-y-10 text-[1.05rem] sm:text-[1.1rem] leading-[1.85]" style={{ color: "#5b3a4a" }}>
        <div>
          <h3 className="font-display font-bold text-2xl mb-3 flex items-center gap-3" style={{ color: "#9c3a50" }}>
            <Heart className="w-5 h-5 flex-shrink-0" fill="#cca300" stroke="#cca300" />
            <span>You are spirit. That's where we begin.</span>
          </h3>
          <p>From that simple truth, we bring together open-hearted people — curious, real, ready — to heal, grow, laugh, and remember who they truly are.</p>
        </div>
        <div>
          <h3 className="font-display font-bold text-2xl mb-3 flex items-center gap-3" style={{ color: "#9c3a50" }}>
            <Heart className="w-5 h-5 flex-shrink-0" fill="#cca300" stroke="#cca300" />
            <span>Come for your well-being. Awaken your spirit. Have a wonderful time doing it.</span>
          </h3>
          <p>Set on New Zealand's breathtaking Coromandel Peninsula, our retreats are part deep spiritual journey, part genuine holiday. Because joy isn't separate from healing — joy <em>is</em> the healing.</p>
          <p className="mt-3">Together we explore consciousness, spiritual awakening, and energy healing — and ask the questions that matter most: <em>Who am I beyond this body, this story, this life? What does it truly mean to open the heart?</em></p>
        </div>
        <div>
          <h3 className="font-display font-bold text-2xl mb-3 flex items-center gap-3" style={{ color: "#9c3a50" }}>
            <Heart className="w-5 h-5 flex-shrink-0" fill="#cca300" stroke="#cca300" />
            <span>You are also on holiday.</span>
          </h3>
          <p>Walk barefoot on golden beaches. Wander ancient kauri forests. Zipline through the forest canopy. Soak in the mineral pools at Lost Springs and let your whole self — physical, mental, emotional, spirit — feel nourished and uplifted. This is a real Coromandel experience — not just sightseeing, but deep immersion in one of New Zealand's most extraordinary places.</p>
        </div>
        <div>
          <h3 className="font-display font-bold text-2xl mb-3 flex items-center gap-3" style={{ color: "#9c3a50" }}>
            <Heart className="w-5 h-5 flex-shrink-0" fill="#cca300" stroke="#cca300" />
            <span>Held with care.</span>
          </h3>
          <p>We keep our gatherings to just 12 people — intentionally. So every person receives real attention, real connection, and a genuine sense of belonging. Not a crowd. A community.</p>
          <p className="mt-3">And the best part? Gathering around the table for home-cooked meals, unhurried conversation, and laughing — really laughing — with people who simply <em>get it</em>.</p>
        </div>
        <div>
          <h3 className="font-display font-bold text-2xl mb-3 flex items-center gap-3" style={{ color: "#9c3a50" }}>
            <Heart className="w-5 h-5 flex-shrink-0" fill="#cca300" stroke="#cca300" />
            <span>This isn't spiritual bootcamp.</span>
          </h3>
          <p>It's what it feels like when your whole self finally gets to show up — where the spirit awakens, the heart expands, and you remember who you are.</p>
        </div>
        <div>
          <h3 className="font-display font-bold text-2xl mb-3 flex items-center gap-3" style={{ color: "#9c3a50" }}>
            <Heart className="w-5 h-5 flex-shrink-0" fill="#cca300" stroke="#cca300" />
            <span>Show up and receive.</span>
          </h3>
          <p>It's an all-inclusive, hassle-free experience with no hidden costs. Meals appear. Transport is sorted. Materials are ready. Nothing is thrown together. Every activity flows into the next with a sense of purpose.</p>
        </div>
      </div>

      <div className="mt-14 text-center">
        <p className="font-script font-bold text-3xl text-coral">{TAGLINE}</p>
        <p className="mt-2 text-sm uppercase tracking-[0.3em] font-bold" style={{ color: "var(--lh-coral)" }}>With love, the Light-Hearted Team</p>
      </div>

      {/* Quote — deep warm aubergine with white text */}
      <div className="mt-14 max-w-2xl mx-auto p-8 sm:p-10 text-center rounded-3xl"
        style={{ background: "#3d2840", boxShadow: "0 25px 50px -25px rgba(61,40,64,0.6)" }}>
        <Quote className="w-8 h-8 mx-auto mb-3" style={{ color: "#ffffff" }} />
        <p className="font-display italic text-2xl sm:text-3xl" style={{ color: "#ffffff" }}>"I came home with an open heart."</p>
        <p className="mt-3 text-sm uppercase tracking-[0.3em]" style={{ color: "#ffffff" }}>— Trish</p>
      </div>
    </div>
  </section>
  <OrganicWave color="var(--lh-cream)" />
  </>
);

/* ============= EXPERIENCE (6 gold hearts) ============= */
export const Experience = () => (
  <>
    <section id="experience" data-testid="experience-section" className="py-24 sm:py-32 bg-[var(--lh-cream)]">
      <div className="container-lh">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="eyebrow" style={{ color: "var(--lh-forest)" }}>The Experience</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-4" style={{ color: "var(--lh-forest)" }}>
            Six ways your heart will open
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
          {EXPERIENCE_CARDS.map((c, i) => (
            <div key={i} data-testid={`experience-card-${i}`} className="flex flex-col items-center text-center">
              <GoldHeart image={IMAGES[c.img]} size={240} />
              <div className="mt-5 pill-dark">{c.heading}</div>
              <div className="mt-3 inline-block px-4 py-1.5 rounded-full text-[0.75rem] uppercase tracking-[0.2em] font-bold"
                style={{ background: "var(--lh-sage)", color: "var(--lh-forest)" }}>
                {c.sub}
              </div>
              <p className="mt-4 text-[0.95rem] leading-relaxed text-wine max-w-xs">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
    <OrganicWave color="#ffffff" flip />
  </>
);

/* ============= RETREAT OPTIONS ============= */
export const Retreats = () => {
  const [openWorkshop, setOpenWorkshop] = useState(null);

  return (
  <>
  <section id="retreats" data-testid="retreats-section" className="py-24 sm:py-32 bg-white">
    <div className="container-lh">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <p className="eyebrow">Choose Your Journey</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-wine mt-4 leading-[1.05]">
          Are you ready to awaken your spirit and have a wonderful time doing it?
        </h2>
        <p className="mt-5 italic font-display text-xl text-plum">No yoga pants required. Just an open heart and an open mind.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
        {/* Mini */}
        <div data-testid="retreat-mini" className="soft-card p-8 sm:p-12 bg-white">
          <div className="flex flex-col items-center">
            <div className="gold-frame w-44 h-44 sm:w-52 sm:h-52 mb-6">
              <img src={IMAGES.miniRetreat} alt="Mini Retreat" className="w-full h-full object-cover" />
            </div>
            <span className="pill-dark">2 Days</span>
            <h3 className="font-display text-3xl sm:text-4xl text-plum mt-4">Mini Retreat</h3>
            <p className="font-display text-4xl sm:text-5xl text-gold mt-2">$750</p>
          </div>
          <p className="mt-7 text-wine leading-relaxed">A deeply nourishing weekend for those ready to pause, go inward, connect to your spirit and return home feeling genuinely different. Two full days of expert-led workshops in a stunning setting.</p>
          <h4 className="mt-7 eyebrow">What's Included</h4>
          <ul className="mt-3 space-y-2.5">
            <Bullet>Saturday morning to Sunday afternoon</Bullet>
            <Bullet>Saturday Spiritual Awakening Workshop</Bullet>
            <Bullet>Sunday Energy Healing & Expansion Workshop</Bullet>
            <Bullet>All materials provided</Bullet>
            <Bullet>Workshop manual to take home</Bullet>
            <Bullet>1 night accommodation at Flaxmill Bay</Bullet>
            <Bullet>4 home-cooked meals (Sat lunch & dinner, Sun breakfast & lunch)</Bullet>
          </ul>
          <button data-testid="cta-reserve-mini" onClick={() => scrollTo("book")} className="btn-primary mt-8">Reserve Your Place</button>
        </div>

        {/* Week */}
        <div data-testid="retreat-week" className="soft-card p-8 sm:p-12 bg-gradient-to-br from-white to-[var(--lh-cream)]">
          <div className="flex flex-col items-center">
            <div className="gold-frame w-44 h-44 sm:w-52 sm:h-52 mb-6">
              <img src={IMAGES.weekImmersion} alt="Week Immersion" className="w-full h-full object-cover" />
            </div>
            <span className="pill-dark">7 Days</span>
            <h3 className="font-display text-3xl sm:text-4xl text-plum mt-4">Full Week Immersion</h3>
            <p className="font-display text-4xl sm:text-5xl text-gold mt-2">$3,900</p>
            <p className="text-xs uppercase tracking-[0.18em] text-plum mt-1">or 20 easy payments of $200</p>
          </div>
          <p className="mt-7 text-wine leading-relaxed">A complete escape into healing, connection, and adventure. Four expert-led workshops woven through a week of road trips, pampering, and magic on the Coromandel.</p>
          <h4 className="mt-7 eyebrow">What's Included</h4>
          <ul className="mt-3 space-y-2.5">
            <Bullet>7 days of fun, learning and connection</Bullet>
            <Bullet>4 Expert-led Workshops: Spiritual Awakening · Energy Healing & Expansion · Awakening to Consciousness · Opening the Heart</Bullet>
            <Bullet>6 nights' accommodation at Flaxmill Bay</Bullet>
            <Bullet>Coromandel road trip & Kauri Grove walk</Bullet>
            <Bullet>Thai massage & Lost Springs mineral pools</Bullet>
            <Bullet>18 home-prepared meals</Bullet>
          </ul>
          <button data-testid="cta-reserve-week" onClick={() => scrollTo("book")} className="btn-primary mt-8">Reserve Your Place</button>
        </div>
      </div>

      {/* For You If… */}
      <div className="mt-20 max-w-4xl mx-auto soft-card p-8 sm:p-12">
        <h3 className="font-display text-3xl sm:text-4xl text-wine">This retreat is for you if…</h3>
        <ul className="mt-6 space-y-3 text-[1.05rem] text-wine">
          <Bullet>You want to learn, grow, and expand your heart, your mind, your spirit.</Bullet>
          <Bullet>You want genuine community, fun, laughter, and walking barefoot on a beach.</Bullet>
          <Bullet>You're ready to stop working <em>on</em> yourself — and start working <em>with</em> yourself.</Bullet>
        </ul>
        <p className="mt-7 text-wine leading-relaxed">
          Spiritual awakening doesn't have to be a mystery. It doesn't have to be complicated. The answers have always been there — right inside your own heart. Let's explore this together, gently guiding you toward your own alignment, consciousness, strength, and power through:
        </p>

        {/* Verna's e-book CTA */}
        <div className="mt-7 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          style={{ background: "var(--lh-sage)", border: "1px solid rgba(40,79,63,0.2)" }}>
          <FileDown className="w-7 h-7 flex-shrink-0" style={{ color: "var(--lh-forest)" }} />
          <p className="text-[0.95rem] leading-relaxed" style={{ color: "var(--lh-forest)" }}>
            Workshops are based on Verna's e-book <em>Wisdom for a Changing Consciousness</em>.{" "}
            <a href={EBOOK_URL} target="_blank" rel="noreferrer" className="underline font-semibold transition" style={{ color: "#1f4ea8" }}>
              Download your free copy →
            </a>
          </p>
        </div>

        {/* Workshop boxes — clickable, colour-coded */}
        <p className="mt-8 text-sm uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--lh-forest)" }}>Click a workshop to read more</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-4">
          {WORKSHOP_BOXES.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => setOpenWorkshop(w.id)}
              data-testid={`workshop-box-${w.id}`}
              className="group flex items-center justify-between gap-4 px-5 py-5 rounded-2xl shadow-[0_8px_25px_-15px_rgba(0,0,0,0.4)] hover:shadow-[0_15px_40px_-15px_rgba(0,0,0,0.5)] hover:-translate-y-1 transition-all text-left"
              style={{ background: w.bg, color: w.text }}
            >
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5 flex-shrink-0" fill={w.text} stroke="none" />
                <span className="font-display text-xl">{w.title}</span>
              </div>
              <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition" />
            </button>
          ))}
        </div>

        <p className="mt-6 italic font-display text-base sm:text-lg text-wine/80">
          Curious to know more? Read the article in the Coromind magazine.{" "}
          <a href={COROMIND_URL} target="_blank" rel="noreferrer" className="underline transition" style={{ color: "#1f4ea8" }}>
            Read on Coromind →
          </a>
        </p>

        <p className="mt-7 text-sm text-wine/80">Workshops are kept intentionally small — limited to 12 people — so you feel truly seen and heard. You'll have space to ask questions and take part in open, honest conversations.</p>

        <div className="mt-10 p-8 sm:p-10 text-center rounded-3xl" style={{ background: "#3d2840", boxShadow: "0 25px 50px -25px rgba(61,40,64,0.6)" }}>
          <Quote className="w-7 h-7 mx-auto mb-2" style={{ color: "#ffffff" }} />
          <p className="font-display italic text-2xl" style={{ color: "#ffffff" }}>"I loved the weekend, everything just flowed naturally. I have learnt to keep coming back to the heart."</p>
          <p className="mt-3 text-sm uppercase tracking-[0.3em]" style={{ color: "#ffffff" }}>— Sarah</p>
        </div>
      </div>
    </div>

    <WorkshopModal
      workshopId={openWorkshop}
      open={!!openWorkshop}
      onOpenChange={(o) => { if (!o) setOpenWorkshop(null); }}
    />
  </section>
  <OrganicWave color="var(--lh-sage)" />
  </>
  );
};

/* ============= ITINERARY ============= */
const ITINERARY = [
  { day: "Day 1 · Saturday", title: "Welcome & Spiritual Awakening", img: IMAGES.day1,
    intro: "Arrive, settle in, and meet the people you'll be sharing this week with. Explore what spiritual awakening means to you personally.",
    bullets: [
      "Activate your innate psychic, spiritual, and healing gifts",
      "No prior experience needed — just an open heart and curious mind",
      "Small group means personal attention and genuine connection",
      "Questions and open conversation warmly encouraged",
      "Explore the paradise setting of Flaxmill Bay and Cooks Beach",
    ], meals: "2 Catered Meals · Lunch & Dinner" },
  { day: "Day 2 · Sunday", title: "Healing & Expansion", img: IMAGES.day2,
    intro: "A full day delving into what it genuinely means to heal and expand — exploring the driving force within that shapes your choices.",
    bullets: [
      "Morning: candid group conversation on the power of thought and belief",
      "Afternoon: live energy healing demonstration with Verna",
      "Opportunity to receive, offer, and witness the healing process firsthand",
      "Hands-on, experiential day of learning and expansion",
    ], meals: "3 Catered Meals · Breakfast, Lunch & Dinner" },
  { day: "Day 3 · Monday", title: "Coromandel Road Trip — Natural Wonders", img: IMAGES.day3,
    intro: "A full day exploring the natural wonders of the Coromandel Peninsula — ancient forests, hidden waterfalls, and a quirky mountain railway.",
    bullets: [
      "Waiau Kauri Grove — walk among magnificent ancient kauri trees",
      "Waiau Falls — crystal-clear water cascading into a native bush swimming hole",
      "Lunch at Coromandel",
      "Driving Creek Railway — mountain train to the stunning Eyefull Tower lookout",
      "Optional zip-lining through the forest canopy (8 exhilarating lines!)",
      "Dinner at Luke's Kitchen, Kuaotunu — iconic seaside dining",
    ], meals: "Breakfast Included · Lunch & Dinner at Own Expense" },
  { day: "Day 4 · Tuesday", title: "Sound Bowls, Picnic & Opening the Heart", img: IMAGES.day4,
    intro: "A day of earth connection, sound healing, and creative self-expression — honouring the balance between inner journey and natural wonder.",
    bullets: [
      "Invigorating bush walk to the top of Shakespeare's Cliff",
      "Earth meditation and picnic lunch with sweeping ocean views",
      "Sound bowl ceremony guided by Deirdre",
      "Afternoon Opening the Heart workshop",
      "Creative art expression — capture your highest, most radiant self",
      "Free time on Flaxmill Bay, Front Beach, and Cooks Beach",
    ], meals: "3 Catered Meals · Breakfast, Lunch & Dinner" },
  { day: "Day 5 · Wednesday", title: "Pamper Day — Loving Your Physical Body", img: IMAGES.day5,
    intro: "A day dedicated entirely to nourishing your body — because your physical self deserves the same love and care as your spirit.",
    bullets: [
      "Traditional Thai massage — pure relaxation and body appreciation",
      "Soap making with essential oils and natural plant-based ingredients",
      "Lunch at a local Whitianga restaurant",
      "Afternoon soak at The Lost Springs geothermal mineral pools",
      "Surrounded by peaceful native forest — quiet space for rest and renewal",
    ], meals: "Breakfast & Dinner Included · Lunch at Own Expense" },
  { day: "Day 6 · Thursday", title: "Awakening to Consciousness", img: IMAGES.day6,
    intro: "Morning workshop followed by a well-deserved free afternoon to explore one of New Zealand's most beloved coastal destinations.",
    bullets: [
      "Guided meditation and exercises — no experience needed",
      "Learn how thoughts, feelings, and beliefs affect your physical body",
      "Connect with your inner guidance and sense your higher awareness",
      "Practice basic energy healing techniques for daily life",
      "Free afternoon — Cathedral Cove coastal track or Hot Water Beach",
      "Farewell dinner together back at Flaxmill Bay",
    ], meals: "3 Catered Meals · Breakfast, Lunch & Dinner" },
  { day: "Day 7 · Friday", title: "Reflections, Gratitude & Farewell", img: IMAGES.day7,
    intro: "A gentle closing to honour everything you've experienced — the insights, the connections, the laughter, and the quiet moments of transformation.",
    bullets: [
      "Closing feedback circle — share as much or as little as you choose",
      "Reflect on insights gained and honour the journey together",
      "Closing celebration",
      "Farewell lunch together",
      "Afternoon close at 1pm",
    ], meals: "2 Catered Meals · Breakfast & Lunch" },
];

export const Itinerary = () => (
  <>
  <section id="itinerary" data-testid="itinerary-section" className="py-24 sm:py-32" style={{ background: "var(--lh-sage)" }}>
    <div className="container-lh">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <p className="eyebrow" style={{ color: "var(--lh-forest)" }}>Day by Day</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4" style={{ color: "var(--lh-forest)" }}>Your Week's Journey</h2>
        <p className="mt-5 italic font-display text-2xl sm:text-3xl" style={{ color: "var(--lh-forest)", opacity: 0.95 }}>Every day is thoughtfully crafted to give you the best experience.</p>
      </div>

      <Accordion type="single" collapsible className="max-w-5xl mx-auto space-y-3">
        {ITINERARY.map((d, i) => (
          <AccordionItem
            key={i}
            value={`day-${i}`}
            className="day-bar bg-white/85 hover:bg-white data-[state=open]:bg-white data-[state=open]:shadow-[0_30px_60px_-40px_rgba(40,79,63,0.45)] rounded-2xl px-5 sm:px-7 border-0"
            data-testid={`itinerary-day-${i + 1}`}
          >
            <AccordionTrigger className="hover:no-underline py-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-left">
                <span className="text-xs uppercase tracking-[0.24em] font-semibold" style={{ color: "var(--lh-forest)" }}>{d.day}</span>
                <span className="font-display text-xl sm:text-2xl text-wine">{d.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid md:grid-cols-2 gap-7 pt-3 pb-6">
                <div className="rounded-xl overflow-hidden h-64 md:h-full">
                  <img src={d.img} alt={d.title} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-wine/90 leading-relaxed">{d.intro}</p>
                  <ul className="mt-5 space-y-2.5">{d.bullets.map((b) => <Bullet key={b}>{b}</Bullet>)}</ul>
                  <div className="mt-6"><span className="pill">{d.meals}</span></div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
  <OrganicWave color="var(--lh-cream)" />
  </>
);

/* ============= STAY ============= */
export const Stay = () => (
  <>
  <section id="stay" data-testid="stay-section" className="bg-[var(--lh-cream)]">
    <div className="h-72 sm:h-[28rem] bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.stayHero})` }} />
    <div className="container-lh py-20 sm:py-24 grid lg:grid-cols-3 gap-12 items-start">
      <div className="lg:col-span-2">
        <p className="eyebrow">Accommodation</p>
        <h2 className="font-display text-4xl sm:text-5xl text-wine mt-4">Flaxmill Bay Accommodation and Venue</h2>
        <p className="font-display italic text-xl text-plum mt-2">Rest, Restore & Refocus at Flaxmill's award-winning accommodation</p>

        <div className="mt-8 space-y-5 text-wine leading-relaxed">
          <p><strong>Flaxmill</strong> is an award-winning accommodation, venue, and campground facility located at Flaxmill Bay near Cooks Beach on the Coromandel Peninsula.</p>
          <p>Set within landscaped park grounds right by the sea, it's conveniently positioned near several Coromandel attractions including Hot Water Beach, Hahei, and Cathedral Cove. The property is within walking distance of the passenger ferry to Whitianga and close to Cooks Beach.</p>
          <p>Accommodation is in a three-bedroom house shared with two others — everyone gets their own bedroom for privacy.</p>
        </div>

        <div className="mt-7 grid grid-cols-3 gap-4 text-center">
          {[{n:"6", h:"2 houses"}, {n:"9", h:"3 houses"}, {n:"12", h:"4 houses"}].map((x) => (
            <div key={x.n} className="bg-white rounded-2xl p-5 border border-[var(--lh-gold)]/30">
              <p className="font-display text-4xl text-plum">{x.n}</p>
              <p className="text-xs uppercase tracking-[0.18em] text-wine mt-1">people = {x.h}</p>
            </div>
          ))}
        </div>

        <a href="https://www.flaxmill.co.nz" target="_blank" rel="noreferrer" data-testid="flaxmill-link" className="mt-7 inline-flex items-center gap-2 underline transition" style={{ color: "#1f4ea8" }}>
          <MapPin className="w-4 h-4" /> www.flaxmill.co.nz · 1031 Purangi Rd, Flaxmill Bay, Cooks Beach
        </a>
      </div>

      <div className="space-y-6">
        <img src={IMAGES.staySmall} alt="Flaxmill Bay Beach" className="rounded-2xl w-full h-56 object-cover shadow-[0_30px_60px_-40px_rgba(91,34,63,0.5)]" />
        <div className="soft-card p-6">
          <Quote className="w-6 h-6 text-gold mb-2" />
          <p className="font-display italic text-lg text-wine leading-snug">"What a perfect place for a retreat — the accommodation was clean and comfortable. It was fun getting to know the others."</p>
          <p className="mt-3 text-xs uppercase tracking-[0.3em] text-plum">— Jeanie</p>
        </div>
      </div>
    </div>
  </section>
  <OrganicWave color="rgba(233,224,241,0.45)" />
  </>
);

/* ============= TEAM ============= */
const teamCard = (img, name, role, bio, link) => (
  <div className="soft-card p-8 sm:p-10 text-center bg-white" data-testid={`team-${name.toLowerCase()}`}>
    <div className="gold-frame w-44 h-44 mx-auto mb-6">
      <img src={img} alt={name} className="w-full h-full object-cover" />
    </div>
    <h3 className="font-display text-3xl text-wine">{name}</h3>
    <p className="text-sm uppercase tracking-[0.24em] text-plum mt-1">{role}</p>
    <p className="mt-5 text-wine/90 leading-relaxed text-[0.98rem] text-left">{bio}</p>
    {link && <a href={`https://${link}`} target="_blank" rel="noreferrer" className="mt-5 inline-block text-plum underline hover:text-coral">{link}</a>}
  </div>
);

export const Team = () => (
  <>
  <section id="team" data-testid="team-section" className="py-24 sm:py-32 bg-[var(--lh-mist)]/30">
    <div className="container-lh">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <p className="eyebrow">Meet Our Team</p>
        <h2 className="font-display text-4xl sm:text-5xl mt-4" style={{ color: "var(--lh-coral)" }}>Light-Hearted Healing Centre & Expansion School</h2>
        <p className="mt-6 italic font-display text-2xl sm:text-3xl text-plum">It's just the two of us — Verna and Deirdre. Let us handle the details: the workshops, the food, the driving. You get to enjoy your holiday with no worries.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
        {teamCard(IMAGES.verna, "Verna", "Workshop Facilitator",
          "Verna has worked with spirit and consciousness all of her adult life. She is an Intuitive Energy Healer, Professional Tarot Card Reader, Workshop Facilitator, and author of Wisdom for a Changing Consciousness. Drawing on 30+ years of experience, Verna works with the high vibrational energy of unconditional love to bring clarity, guidance, and healing — empowering you to create positive change from within. Her approach is warm and easy-going.",
          VERNA_SITE
        )}
        {teamCard(IMAGES.deirdre, "Deirdre", "Catering & Sound Bowl Healing",
          "Deirdre's background is music and retail. She is a certified SoulNar sound healing practitioner, using the transformative power of crystal singing bowls to promote deep healing and restoration — vibrations that quiet the nervous system and restore the body's natural state of balance. Her other passion is cooking and looking after people. She brings warmth, humour, and nourishment in equal measure."
        )}
      </div>
      <p className="mt-12 max-w-3xl mx-auto text-center text-wine font-display italic text-2xl sm:text-3xl">
        There may be an opportunity to book a private tarot card reading or energy healing session with Verna, and/or a private sound bowl healing with Deirdre.
      </p>
    </div>
  </section>
  <OrganicWave color="#ffffff" />
  </>
);

/* ============= EXPLORE / MAP ============= */
export const Explore = () => (
  <section id="explore" data-testid="explore-section" className="py-24 sm:py-32 bg-white">
    <div className="container-lh">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <p className="eyebrow">Explore</p>
        <h2 className="font-display text-4xl sm:text-5xl text-wine mt-4">Discover New Zealand's Breathtaking Coromandel Peninsula</h2>
      </div>
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 rounded-3xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(91,34,63,0.45)] border border-[var(--lh-gold)]/30">
          <iframe
            title="Flaxmill Bay Map"
            className="w-full h-[420px] sm:h-[520px]"
            src="https://www.google.com/maps?q=Flaxmill+Bay+Holiday+Accommodation,+1031+Purangi+Rd,+Cooks+Beach,+New+Zealand&t=&z=13&ie=UTF8&iwloc=B&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <div className="lg:col-span-2 soft-card p-8 self-center">
          <h3 className="font-display text-2xl text-wine mb-4">How do I get there?</h3>
          <p className="leading-relaxed" style={{ color: "#5b3a4a" }}>
            The venue is about <strong>120km from Thames</strong> — roughly 1.5–2 hours depending on traffic. From Thames, take the Kopu–Hikuai Road (SH25A), follow signs to Whitianga until Whenuakite, then turn towards Hot Water Beach / Hahei / Cooks Beach. Turn onto Purangi Road heading towards Ferry Landing — Flaxmill Bay accommodation is on your left, past Shakespeare's Cliff.
          </p>
          <p className="mt-4 leading-relaxed" style={{ color: "#5b3a4a" }}>
            You can also fly or shuttle from Auckland to Whitianga, then take the passenger ferry ($5 one way / $8 return) across to Ferry Landing. Flaxmill Bay accommodation is about 2km from Ferry Landing. It's an easy walk, or a pickup can be arranged for $10.{" "}
            <button onClick={() => scrollTo("book")} className="underline" style={{ color: "#1f4ea8" }}>Contact us</button> to make arrangements.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============= DATES / CALENDAR ============= */
export const Dates = () => {
  const [retreats, setRetreats] = useState([]);
  const [selected, setSelected] = useState(undefined);

  useEffect(() => {
    axios.get(`${API}/retreats`).then((r) => setRetreats(r.data)).catch(() => {});
  }, []);

  const highlighted = retreats.flatMap((r) => {
    const start = new Date(r.start);
    const end = new Date(r.end);
    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) days.push(new Date(d));
    return days;
  });

  return (
    <section id="dates" data-testid="dates-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.calendarBg})` }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(91,34,63,0.55) 0%, rgba(91,34,63,0.65) 100%)" }} />
      <div className="relative container-lh text-white">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <h2 className="font-display text-4xl sm:text-5xl text-white" style={{ textShadow: "0 4px 18px rgba(0,0,0,0.5)" }}>Retreat Calendar</h2>
          <h2 className="font-display text-4xl sm:text-5xl text-white mt-3" style={{ textShadow: "0 4px 18px rgba(0,0,0,0.5)" }}>2026 Available Retreats</h2>
          <p className="mt-6 italic font-display text-2xl sm:text-3xl" style={{ color: "#cca300", textShadow: "0 2px 10px rgba(0,0,0,0.5)" }}>
            We run small, intimate retreats throughout the year. Spaces are limited — we recommend booking early.
          </p>
          <p className="mt-4 text-white/95 max-w-2xl mx-auto" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.5)" }}>
            Each retreat is timed with either a full moon or new moon — because some things are just better aligned with the natural world.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="space-y-6">
            <div className="rounded-3xl p-6 sm:p-8 shadow-2xl" style={{ background: "var(--lh-butter)", color: "var(--lh-forest)" }}>
              <Calendar
                mode="single"
                selected={selected}
                onSelect={setSelected}
                modifiers={{ retreat: highlighted }}
                modifiersClassNames={{ retreat: "retreat-week" }}
                className="mx-auto"
                data-testid="retreat-calendar"
              />
              <p className="text-xs text-center mt-3" style={{ color: "var(--lh-forest)" }}>
                Green-highlighted weeks indicate scheduled retreats. Click any date to explore.
              </p>
            </div>

            {/* White text box — placed below calendar, bold orange writing */}
            <div className="rounded-2xl bg-white px-6 py-6 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
              <p className="font-display italic font-bold text-lg sm:text-xl leading-relaxed" style={{ color: "var(--lh-coral)" }}>
                Light-Hearted Healing Centre and Expansion School is inclusive of all experiences. Open up to receiving and expansion in every sense of the universe, because you are open to Source — and Source does not know lack or limitation.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {retreats.map((r, i) => (
              <button
                key={i}
                data-testid={`retreat-date-${i}`}
                onClick={() => scrollTo("book")}
                className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/25 backdrop-blur-md transition group text-left"
              >
                <div className="flex items-center gap-4">
                  {r.moon === "full"
                    ? <Sun className="w-6 h-6 text-[var(--lh-gold)]" />
                    : <Moon className="w-6 h-6 text-[var(--lh-butter)]" />}
                  <div>
                    <p className="font-display text-lg sm:text-xl">{r.label}</p>
                    <p className="text-xs uppercase tracking-[0.24em] text-white/80">{r.moon === "full" ? "Full Moon" : "New Moon"} · {r.spots_left} spots left</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-[var(--lh-gold)] group-hover:translate-x-1 transition" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============= NEWSLETTER SIGNUP (inside Reserve Your Place) ============= */
export const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim(), name: name.trim(), source: "reserve-your-place" });
      toast.success("You're on the list ✦", {
        description: "Look out for special offers, new dates and heartfelt updates from the Coromandel.",
      });
      setEmail("");
      setName("");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email vernajcarr@gmail.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="newsletter-signup"
      className="mt-16 max-w-3xl mx-auto rounded-3xl p-8 sm:p-12 text-center"
      style={{
        background: "linear-gradient(135deg, #5b223f 0%, #933551 100%)",
        boxShadow: "0 35px 70px -30px rgba(91,34,63,0.55)",
        border: "1px solid rgba(204,163,0,0.35)",
      }}
    >
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-5"
        style={{ background: "rgba(204,163,0,0.18)", border: "1px solid rgba(204,163,0,0.55)" }}>
        <Gift className="w-7 h-7" style={{ color: "var(--lh-gold)" }} />
      </div>
      <p className="eyebrow" style={{ color: "var(--lh-gold)" }}>Free Newsletter</p>
      <h3 className="font-display text-3xl sm:text-4xl mt-3 text-white">
        Special offers, gentle inspiration &amp; new retreat dates
      </h3>
      <p className="mt-4 max-w-xl mx-auto text-[1rem] sm:text-[1.05rem] leading-relaxed" style={{ color: "#fbcabf" }}>
        Pop your email in and we'll send the occasional heartfelt note — early-bird pricing, fresh dates,
        and a wee bit of light-hearted wisdom from the Coromandel. No spam, just love. Unsubscribe anytime.
      </p>

      <form onSubmit={handleSubscribe} className="mt-7 grid sm:grid-cols-[1fr_1.4fr_auto] gap-3 max-w-2xl mx-auto">
        <input
          data-testid="newsletter-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="First name (optional)"
          className="rounded-full px-5 py-3.5 bg-white/95 text-wine placeholder:text-plum/60 focus:outline-none focus:ring-2 focus:ring-[var(--lh-gold)] text-[0.95rem]"
        />
        <input
          data-testid="newsletter-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          className="rounded-full px-5 py-3.5 bg-white/95 text-wine placeholder:text-plum/60 focus:outline-none focus:ring-2 focus:ring-[var(--lh-gold)] text-[0.95rem]"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="newsletter-submit"
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm uppercase tracking-[0.16em] transition-all hover:-translate-y-0.5 disabled:opacity-60"
          style={{
            background: "var(--lh-gold)",
            color: "#5b223f",
            boxShadow: "0 12px 30px -10px rgba(204,163,0,0.55)",
          }}
        >
          {submitting ? "Sending…" : "Sign Me Up"} <Send className="w-4 h-4" />
        </button>
      </form>

      <p className="mt-5 text-[0.72rem] uppercase tracking-[0.24em]" style={{ color: "rgba(251,202,191,0.85)" }}>
        ✦ Free forever · A few emails a year · Your details stay with us ✦
      </p>
    </div>
  );
};

/* ============= PRICING ============= */
export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="py-24 sm:py-32 bg-white">
    <div className="container-lh max-w-5xl">
      <div className="text-center mb-14">
        <p className="eyebrow">Pricing & Booking</p>
        <h2 className="font-display text-4xl sm:text-5xl text-wine mt-4">Reserve Your Place</h2>
        <p className="mt-4 text-wine/80 max-w-2xl mx-auto">Two beautifully crafted experiences. Both include healthy meals, expert guidance, and luxury accommodation at Flaxmill Bay.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-8 items-stretch">
        <div className="soft-card p-10 text-center flex flex-col" data-testid="pricing-mini">
          <span className="pill-dark mx-auto">Weekend</span>
          <h3 className="font-display text-3xl text-plum mt-4">Mini Retreat</h3>
          <p className="font-display text-6xl text-gold mt-3">$750</p>
          <p className="text-sm text-wine/70 mt-1">2 days · 1 night · 4 meals</p>
          <div className="mt-auto pt-7">
            <button onClick={() => scrollTo("book")} className="btn-primary w-full justify-center" data-testid="pricing-mini-cta">Reserve Now</button>
          </div>
        </div>
        <div className="soft-card p-10 text-center bg-gradient-to-br from-white to-[var(--lh-cream)] border-2 border-[var(--lh-gold)] flex flex-col" data-testid="pricing-week">
          <span className="pill-gold mx-auto">Most Loved</span>
          <h3 className="font-display text-3xl text-plum mt-4">Full Week Immersion</h3>
          <p className="font-display text-6xl text-gold mt-3">$3,900</p>
          <p className="text-sm text-wine/80 mt-1">7 days · 6 nights · 18 meals</p>
          <p className="mt-4 inline-block px-4 py-2 rounded-full bg-[var(--lh-blush)]/40 text-plum text-sm font-medium mx-auto">
            Easy payments available — 20 × $200
          </p>
          <div className="mt-auto pt-7">
            <button onClick={() => scrollTo("book")} className="btn-primary w-full justify-center" data-testid="pricing-week-cta">Reserve Now</button>
          </div>
        </div>
      </div>
      <p className="mt-10 text-center text-sm text-wine/70">Payment by credit card or bank transfer. Cancellation policy applies.</p>

      <NewsletterSignup />
    </div>
  </section>
);

/* ============= TESTIMONIALS (gold-heart quotes) ============= */
const TESTIMONIALS = [
  { q: "The Light-Hearted team made us all feel comfortable, that we were safe and it was ok to express ourselves.", a: "Claire" },
  { q: "I thought the workshops went deep — we were authentic.", a: "Jack" },
  { q: "The sound bowls were amazing. I could feel the vibration go right through my body.", a: "Andy" },
  { q: "I felt so loved by spirit and at peace. I'm learning to love myself, forgive myself, and make new choices.", a: "Suzanne" },
  { q: "Verna, thank you — I am still feeling the positive effects of your healing weeks later. You are amazing and have a real gift.", a: "Caroline · Whitianga" },
  { q: "What we did in the workshop confirms that my gut instinct can be trusted.", a: "Rebecca" },
];

export const Testimonials = () => (
  <>
  <section id="testimonials" data-testid="testimonials-section" className="relative py-24 sm:py-32 overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.testimonialBg})` }} />
    <div className="relative container-lh text-white">
      <div className="text-center mb-14">
        <p className="eyebrow" style={{ color: "var(--lh-butter)", textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>People Are Saying</p>
        <h2 className="font-display text-4xl sm:text-5xl mt-4" style={{ textShadow: "0 3px 18px rgba(0,0,0,0.65)" }}>Words from the Heart</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-6 justify-items-center">
        {TESTIMONIALS.map((t, i) => (
          <div key={i} data-testid={`testimonial-${i}`} className="w-full max-w-[360px]">
            <GoldHeart size={340}>
              <div className="px-3 max-w-[250px]">
                <Quote className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--lh-gold)" }} />
                <p className="font-display italic text-[1.12rem] sm:text-[1.15rem] leading-[1.4] font-medium" style={{ color: "var(--lh-wine)" }}>
                  "{t.q}"
                </p>
                <p className="mt-3 text-[0.78rem] uppercase tracking-[0.3em] font-bold" style={{ color: "var(--lh-plum)" }}>— {t.a}</p>
              </div>
            </GoldHeart>
          </div>
        ))}
      </div>
    </div>
  </section>
  <OrganicWave color="var(--lh-cream)" />
  </>
);

/* ============= FAQ ============= */
const FAQS = [
  { q: "What spiritual tradition or background underpins the workshops?", img: IMAGES.faq1,
    a: "The workshops are heart-centred and consciousness-based, exploring themes such as self-love, worthiness, gratitude, awakening your spiritual gifts, understanding energy, and connecting with your authentic self. The approach is open, non-dogmatic, and welcoming to all backgrounds — no prior experience required. You will feel safe and supported to have whatever experience aligns with you." },
  { q: "Do I need any prior spiritual experience?", img: IMAGES.faq2,
    a: "Not at all. The workshops are designed for beginners, though they can go as deep as you want to go. All you need is an open mind and a willingness to explore." },
  { q: "Who are the facilitators?", img: IMAGES.faq3,
    a: "Verna has worked with spirit and consciousness all of her adult life. She is a professional tarot card reader, energy healing facilitator, and over the years she has led many meditation groups and workshops. Visit vernacarrspiritualhealing.com" },
  { q: "Is this suitable for solo travellers?", img: IMAGES.faq4,
    a: "Absolutely. Many participants come on their own and find it a wonderful way to meet kindred spirits. The small group size and shared meals make connection natural and easy." },
  { q: "What should I bring?", img: IMAGES.faq5,
    a: "An open heart and an open mind. Comfortable clothing, a hat, and shoes suitable for workshops, walking, and time in nature; a raincoat, a swimsuit, sunscreen, and insect repellent. All workshop materials and craft supplies are provided." },
  { q: "Will there be time for rest and solitude?", img: IMAGES.faq6,
    a: "Yes — the programme is intentionally designed to balance group workshops and activities with personal free time. Some of the most valuable moments happen when you're simply wandering along a beach on your own." },
  { q: "How do I get in touch with questions?", img: IMAGES.faq7,
    a: "Phone Verna on 027 320 0079 or email vernajcarr@gmail.com. Verna is warm and happy to answer any questions before you book." },
  { q: "How do I get there?", img: IMAGES.faq8,
    a: "The venue is about 120km from Thames — roughly 1.5–2 hours depending on traffic. From Thames, take the Kopu–Hikuai Road (SH25A), follow signs to Whitianga until Whenuakite, then turn towards Hot Water Beach / Hahei / Cooks Beach. You can also fly or shuttle from Auckland to Whitianga, then take the passenger ferry ($5 one way / $8 return) across to Ferry Landing." },
];

export const FAQ = () => (
  <section id="faq" data-testid="faq-section" className="py-24 sm:py-32 bg-[var(--lh-cream)]">
    <div className="container-lh max-w-5xl">
      <div className="text-center mb-12">
        <p className="eyebrow">Frequently Asked</p>
        <h2 className="font-display text-4xl sm:text-5xl text-wine mt-4">Questions, Answered</h2>
      </div>
      <Accordion type="single" collapsible className="space-y-3">
        {FAQS.map((f, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="day-bar bg-white hover:bg-[var(--lh-blush)]/30 data-[state=open]:bg-white rounded-2xl px-5 sm:px-7 border-0"
            data-testid={`faq-${i}`}
          >
            <AccordionTrigger className="hover:no-underline py-5 font-display text-lg sm:text-xl text-wine text-left">{f.q}</AccordionTrigger>
            <AccordionContent>
              <div className="grid md:grid-cols-3 gap-6 pt-2 pb-5 items-center">
                <div className="md:col-span-1 rounded-xl overflow-hidden h-52 flex items-center justify-center" style={{ background: i === 0 ? "transparent" : "#f1e3e0" }}>
                  <img src={f.img} alt="" className={i === 0 ? "h-36 w-auto object-contain logo-clean" : "w-full h-full object-contain"} />
                </div>
                <p className="md:col-span-2 text-wine leading-relaxed">{f.a}</p>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ============= BLOG / STORIES ============= */
export const Blog = () => {
  const posts = BLOG_POSTS || [];
  return (
    <section id="blog" data-testid="blog-section" className="py-24 sm:py-32 bg-[var(--lh-cream)]">
      <div className="container-lh max-w-5xl">
        <div className="text-center mb-12">
          <p className="eyebrow">Journal</p>
          <h2 className="font-display text-4xl sm:text-5xl text-wine mt-4">Stories & Reflections</h2>
          <p className="mt-4 text-wine/80 max-w-2xl mx-auto italic">
            Gentle musings, retreat highlights, and a little light-hearted wisdom from the Coromandel.
          </p>
        </div>

        {posts.length === 0 ? (
          <div data-testid="blog-empty" className="max-w-2xl mx-auto soft-card p-10 sm:p-14 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
              style={{ background: "rgba(204,163,0,0.15)", border: "1px solid rgba(204,163,0,0.45)" }}>
              <BookOpen className="w-8 h-8" style={{ color: "var(--lh-gold)" }} />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-plum">New stories coming soon</h3>
            <p className="mt-4 text-wine leading-relaxed">
              Verna is gathering her thoughts and will be sharing reflections, retreat memories,
              and quiet wisdom here in the coming months.
            </p>
            <p className="mt-3 text-wine leading-relaxed">
              Pop your email into the newsletter (in <em>Reserve Your Place</em> above) and we'll let you know the moment the first one lands.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p) => (
              <article
                key={p.id}
                data-testid={`blog-post-${p.id}`}
                className="soft-card overflow-hidden bg-white flex flex-col"
              >
                {p.image && (
                  <div className="h-52 w-full overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--lh-coral)" }}>
                    {p.date}
                  </p>
                  <h3 className="font-display text-2xl text-wine mt-2 leading-tight">{p.title}</h3>
                  <p className="mt-3 text-wine/85 text-[0.95rem] leading-relaxed flex-1">{p.excerpt}</p>

                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    {p.pdfUrl && (
                      <a
                        href={p.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        download
                        data-testid={`blog-download-${p.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition hover:-translate-y-0.5"
                        style={{ background: "var(--lh-gold)", color: "#5b223f" }}
                      >
                        <FileDown className="w-4 h-4" /> Download PDF
                      </a>
                    )}
                    {p.readUrl && (
                      <a
                        href={p.readUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold underline"
                        style={{ color: "#1f4ea8" }}
                      >
                        Read more <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ============= POLICY CONTENT (Privacy + Cancellation) ============= */
const PRIVACY_POLICY = {
  title: "Privacy Policy",
  intro: "Your privacy matters to us. Here's exactly what we do — and don't do — with your details.",
  sections: [
    {
      heading: "What we collect",
      body: "When you contact us, fill in the enquiry form, or subscribe to our newsletter, we collect only what we need: your name, email address, phone number (if you provide it), and any message you send. We do not collect anything else.",
    },
    {
      heading: "How we use it",
      body: "Your details are used solely to reply to your enquiry, share retreat information, and (if you've opted in) send the occasional newsletter with retreat dates and special offers. That is all.",
    },
    {
      heading: "We never sell or share your information",
      body: "Your details are never sold, traded, or passed to any third party for marketing. They live securely on our website database and in Verna's email inbox — nowhere else.",
    },
    {
      heading: "Your rights",
      body: "You can ask us at any time to see, correct, or delete the information we hold about you. Just email vernajcarr@gmail.com and we'll respond within a few days. You can unsubscribe from the newsletter anytime — every email includes an unsubscribe link.",
    },
    {
      heading: "Cookies",
      body: "Our website uses minimal cookies, only for things like remembering you've already visited. We do not run advertising trackers.",
    },
    {
      heading: "Questions?",
      body: "If you'd like to know more, just phone Verna on 027 320 0079 or email vernajcarr@gmail.com. We're happy to chat.",
    },
  ],
};

const CANCELLATION_POLICY = {
  title: "Cancellation Policy",
  intro: "We understand life happens. Here's how cancellations work — fair to you and fair to us.",
  sections: [
    {
      heading: "Deposit",
      body: "A non-refundable deposit of $500 (Mini Retreat) or $1,000 (Full Week Immersion) secures your place. The balance is due 30 days before your retreat begins.",
    },
    {
      heading: "More than 60 days before the retreat",
      body: "Full refund of any payments made, less the non-refundable deposit. Or — your full payment can be transferred to a future retreat date within the next 12 months.",
    },
    {
      heading: "Between 30 and 60 days before",
      body: "50% of the retreat cost is refundable (less the deposit). Alternatively, your full payment can be transferred to another person of your choosing, or to a future retreat within 12 months.",
    },
    {
      heading: "Less than 30 days before",
      body: "No refund is available, but your payment can be transferred to another person of your choosing, or held as a credit for a future retreat within 12 months.",
    },
    {
      heading: "If we have to cancel",
      body: "In the rare event we have to cancel a retreat (illness, weather, or anything unforeseen), you'll receive a full refund of everything you've paid — including the deposit — or the option to transfer to a future retreat.",
    },
    {
      heading: "Travel insurance",
      body: "We strongly recommend taking out travel insurance once you book, especially if you're travelling from outside New Zealand. It's the easiest way to cover yourself for unexpected changes.",
    },
    {
      heading: "Need to talk?",
      body: "If something has come up, please reach out — Verna on 027 320 0079 or vernajcarr@gmail.com. We'll always do our best to find a kind solution.",
    },
  ],
};

const PolicyDialog = ({ open, onOpenChange, policy }) => {
  if (!policy) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0 rounded-3xl border-0">
        <div className="px-7 sm:px-10 pt-8 pb-4" style={{ background: "#f1e3e0" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl sm:text-4xl" style={{ color: "#5b223f" }}>
              {policy.title}
            </DialogTitle>
            <DialogDescription className="font-display italic text-base sm:text-lg mt-2" style={{ color: "#5b223f" }}>
              {policy.intro}
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="px-7 sm:px-10 py-8 space-y-6 bg-white">
          {policy.sections.map((s, i) => (
            <div key={i}>
              <h4 className="font-display text-xl mb-2 flex items-center gap-2" style={{ color: "#9c3a50" }}>
                <Heart className="w-4 h-4 flex-shrink-0" fill="#cca300" stroke="#cca300" />
                {s.heading}
              </h4>
              <p className="text-[0.97rem] leading-relaxed" style={{ color: "#5b3a4a" }}>{s.body}</p>
            </div>
          ))}
          <p className="text-xs text-center pt-4 border-t italic" style={{ color: "var(--lh-plum)", borderColor: "rgba(91,34,63,0.18)" }}>
            Last updated: {new Date().toLocaleDateString("en-NZ", { month: "long", year: "numeric" })}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============= FOOTER ============= */
export const Footer = () => {
  const [policy, setPolicy] = useState(null);
  return (
    <footer id="footer" data-testid="site-footer" className="relative">
      <div className="h-72 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.footerStretch})` }} />
      <div className="py-16" style={{ background: "linear-gradient(180deg, #f1e3e0 0%, #fbcabf 100%)" }}>
        <div className="container-lh text-center">
          <div className="flex justify-center mb-5">
            <img src={IMAGES.logo} alt="Light-Hearted Healing Centre logo" className="w-36 h-36 sm:w-44 sm:h-44 object-contain gentle-pulse logo-clean" />
          </div>
          <h3 className="font-display text-3xl sm:text-4xl" style={{ color: "var(--lh-wine)" }}>Thank You from the Light-Hearted Team</h3>
          <p className="font-script text-3xl mt-3" style={{ color: "var(--lh-coral)" }}>{TAGLINE}</p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 text-xl sm:text-3xl font-display">
            <a href="tel:+64273200079" className="inline-flex items-center gap-3 transition font-bold" style={{ color: "var(--lh-wine)" }} data-testid="footer-phone">
              <Phone className="w-7 h-7 sm:w-8 sm:h-8" /> Verna · +64 027 320 0079
            </a>
            <a href="mailto:vernajcarr@gmail.com" className="inline-flex items-center gap-3 transition font-bold" style={{ color: "var(--lh-wine)" }} data-testid="footer-email">
              <Mail className="w-7 h-7 sm:w-8 sm:h-8" /> vernajcarr@gmail.com
            </a>
          </div>

          {/* Social — Verna's personal Facebook */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <a
              href="https://www.facebook.com/verna.carr"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-facebook"
              aria-label="Verna Carr on Facebook"
              className="inline-flex items-center justify-center w-12 h-12 rounded-full transition hover:-translate-y-0.5"
              style={{ background: "var(--lh-wine)", color: "var(--lh-butter)", boxShadow: "0 10px 25px -12px rgba(91,34,63,0.5)" }}
            >
              <Facebook className="w-6 h-6" />
            </a>
            <span className="text-sm sm:text-base font-display italic" style={{ color: "var(--lh-wine)" }}>
              Follow Verna on Facebook
            </span>
          </div>

          {/* Policy links */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm" style={{ color: "var(--lh-plum)" }}>
            <button
              type="button"
              onClick={() => setPolicy(PRIVACY_POLICY)}
              data-testid="footer-privacy-link"
              className="underline-offset-4 hover:underline transition font-medium"
            >
              Privacy Policy
            </button>
            <span style={{ color: "rgba(91,34,63,0.35)" }}>·</span>
            <button
              type="button"
              onClick={() => setPolicy(CANCELLATION_POLICY)}
              data-testid="footer-cancellation-link"
              className="underline-offset-4 hover:underline transition font-medium"
            >
              Cancellation Policy
            </button>
            <span style={{ color: "rgba(91,34,63,0.35)" }}>·</span>
            <a
              href={`https://${VERNA_SITE}`}
              target="_blank"
              rel="noreferrer"
              className="underline-offset-4 hover:underline transition font-medium"
              style={{ color: "var(--lh-plum)" }}
            >
              vernacarrspiritualhealing.com
            </a>
          </div>

          <div className="mt-8 pt-6 border-t text-xs uppercase tracking-[0.3em]" style={{ borderColor: "rgba(91,34,63,0.18)", color: "var(--lh-plum)" }}>
            © {new Date().getFullYear()} Light-Hearted Healing Centre & Expansion School · Coromandel, New Zealand
          </div>
        </div>
      </div>

      <PolicyDialog
        open={!!policy}
        onOpenChange={(o) => { if (!o) setPolicy(null); }}
        policy={policy}
      />
    </footer>
  );
};
